import { DayPhase } from "game-common/mechanics/physics_mechanics"
import { RoomAmbience, Visibility } from "game-common/models"
import { TwoStoreyHouse } from "game-common/room/ember/two_storey_house"

/** Shared client state for the active room and all of its passive floor views. */
export class SceneContext {
    id?: string
    generation = 0
    private roomIds: string[] = []
    dayPhase: DayPhase | null = null
    brightness = 0
    ambience: RoomAmbience | null = null
    private brightnessInterval?: ReturnType<typeof setInterval>

    constructor(private paintBrightness: (brightness: number) => void) {}

    enter(roomId: string, house: TwoStoreyHouse | undefined, phase?: DayPhase, visibility?: Visibility): boolean {
        const id = house?.groundRoomId ?? roomId
        const changed = id !== this.id
        this.id = id
        this.roomIds = house?.floorRoomIds ?? [id, house?.upperRoomId, roomId].filter(Boolean)
        if (changed) {
            this.generation++
            this.stopFade()
            this.dayPhase = phase ? { ...phase } : null
            this.brightness = phase?.brightness ?? visibility?.darkness ?? 0
        } else if (phase) {
            this.updateDayPhase(phase)
        }
        return changed
    }

    contains(roomId?: string): boolean { return !roomId || this.roomIds.includes(roomId) }

    get daylight(): number | undefined { return this.dayPhase ? this.brightness : undefined }

    setAmbience(ambience: RoomAmbience): void {
        // Repeated floor-entry packets describe the same weather. Keep its configuration stable.
        if (JSON.stringify(ambience) !== JSON.stringify(this.ambience)) this.ambience = ambience
    }

    visibility(visibility: Visibility): Visibility {
        return this.dayPhase && !visibility?.fullOcclusion
            ? { ...visibility, darkness: this.brightness } : visibility
    }

    updateDayPhase(phase?: DayPhase): void {
        if (!phase || (phase.name === this.dayPhase?.name && phase.brightness === this.dayPhase?.brightness)) return
        this.dayPhase = { ...phase }
        this.stopFade()
        const target = phase.brightness
        this.brightnessInterval = setInterval(() => {
            const distance = target - this.brightness
            this.brightness += Math.sign(distance) * Math.min(.01, Math.abs(distance))
            if (Math.abs(target - this.brightness) <= .01) {
                this.brightness = target
                this.stopFade()
            }
            this.paintBrightness(this.brightness)
        }, 60)
    }

    reset(): void {
        this.generation++
        this.stopFade()
        this.id = undefined
        this.roomIds = []
        this.dayPhase = null
        this.brightness = 0
        this.ambience = null
    }

    private stopFade(): void {
        clearInterval(this.brightnessInterval)
        this.brightnessInterval = undefined
    }
}
